import { useMemo } from 'react';

/**
 * Shared hierarchy cycle-guard. Given a flat list of tree nodes and the id of the
 * node currently being edited, returns the set of ids that may NOT be its parent —
 * itself plus all of its descendants (selecting any of them would create a cycle).
 *
 * Pure + reusable across every parent/child form (Authority, Location, Department,
 * and registry-hierarchical types). Extracted from the duplicated `forbiddenParentIds`
 * useMemo that lived inline in AuthorityForm / LocationForm.
 */
export function computeForbiddenParentIds<T extends Record<string, any>>(
  items: T[],
  selfId: any,
  idKey: string = 'id',
  parentKey: string = 'parentId',
): Set<any> {
  const forbidden = new Set<any>();
  if (selfId === null || selfId === undefined) return forbidden; // creating new → nothing forbidden
  forbidden.add(selfId);
  const stack: any[] = [selfId];
  while (stack.length) {
    const cur = stack.pop();
    for (const it of items) {
      if (it[parentKey] === cur && !forbidden.has(it[idKey])) {
        forbidden.add(it[idKey]);
        stack.push(it[idKey]);
      }
    }
  }
  return forbidden;
}

export interface CycleGuard {
  /** ids that cannot be chosen as parent (self + descendants). */
  forbiddenIds: Set<any>;
  /** Returns an error message if `parentId` would create a cycle, else undefined. */
  cycleError: (parentId: any, labels?: { self?: string; descendant?: string }) => string | undefined;
}

export function useTreeCycleGuard<T extends Record<string, any>>(
  items: T[],
  selfId: any,
  opts?: { idKey?: string; parentKey?: string },
): CycleGuard {
  const idKey = opts?.idKey ?? 'id';
  const parentKey = opts?.parentKey ?? 'parentId';

  const forbiddenIds = useMemo(
    () => computeForbiddenParentIds(items, selfId, idKey, parentKey),
    [items, selfId, idKey, parentKey],
  );

  const cycleError = (
    parentId: any,
    labels?: { self?: string; descendant?: string },
  ): string | undefined => {
    if (parentId === null || parentId === undefined || selfId === null || selfId === undefined) return undefined;
    if (!forbiddenIds.has(parentId)) return undefined;
    return parentId === selfId
      ? (labels?.self ?? 'An item cannot be its own parent.')
      : (labels?.descendant ?? 'Cannot select a descendant — that would create a hierarchy cycle.');
  };

  return { forbiddenIds, cycleError };
}
